<template>
  <div>
    <b-row class="mb-2">
      <b-col md="6">
        <h4 class="font-weight-bold ml-1 mb-1">Mannskapsplanlegger</h4>
        <p class="text-muted small ml-1 mb-0">
          Planlagt mannskap per fartøy og ordre. Mannskapet skrives automatisk til ordrene.
        </p>
      </b-col>
      <b-col md="6" class="text-md-right mt-2 mt-md-0">
        <b-button variant="outline-primary" class="mr-2" @click="openChange(null, null)">
          <i class="ion ion-ios-medkit mr-1"></i>Registrer fravær
        </b-button>
        <b-button variant="default" :disabled="isLoading" @click="load()">
          <i class="ion ion-ios-refresh"></i>
        </b-button>
      </b-col>
    </b-row>

    <!-- Toolbar -->
    <b-card no-body class="mb-3">
      <b-card-body class="py-2 d-flex flex-wrap align-items-center">
        <b-button-group size="sm" class="mr-3 my-1">
          <b-button variant="default" @click="shift(-1)" title="Forrige uke">
            <i class="ion ion-ios-arrow-back"></i>
          </b-button>
          <b-button variant="default" @click="goToday()">I dag</b-button>
          <b-button variant="default" @click="shift(1)" title="Neste uke">
            <i class="ion ion-ios-arrow-forward"></i>
          </b-button>
        </b-button-group>

        <span class="font-weight-bold mr-3 my-1">{{ rangeLabel }}</span>

        <b-form-select v-model="weeks" :options="weekOptions" size="sm" class="mr-2 my-1 w-auto" @change="load()" />

        <b-form-select v-model="vesselFilter" :options="vesselOptions" size="sm" class="mr-2 my-1 w-auto" />

        <div class="ml-auto my-1 d-flex flex-wrap align-items-center small">
          <span v-for="s in legendSkifts" :key="s.id" class="mr-3 d-inline-flex align-items-center">
            <span class="cp-legend-swatch mr-1" :style="{ background: s.color }"></span>{{ s.name }}
          </span>
          <span class="mr-3 d-inline-flex align-items-center">
            <span class="cp-legend-swatch cp-legend-unmanaged mr-1"></span>Ikke planstyrt
          </span>
        </div>
      </b-card-body>
    </b-card>

    <b-tabs content-class="mt-3" v-model="tab">
      <!-- Timeline -->
      <b-tab title="Tidslinje" active>
        <div v-if="isLoading && !plan" class="text-center text-muted py-5">
          <b-spinner small class="mr-2"></b-spinner>Laster plan …
        </div>

        <div v-else-if="plan && visibleVessels.length === 0" class="text-center text-muted py-5">
          Ingen fartøy å vise.
        </div>

        <div v-else-if="plan" class="cp-scroll border rounded bg-white" :class="{ 'cp-loading': isLoading }">
          <div class="cp-grid" :style="{ width: nameWidth + days.length * dayWidth + 'px' }">
            <!-- Header: weeks -->
            <div class="cp-row cp-head">
              <div class="cp-name cp-sticky" :style="{ width: nameWidth + 'px' }"></div>
              <div
                v-for="w in weekHeaders"
                :key="w.key"
                class="cp-week"
                :style="{ width: w.days * dayWidth + 'px' }"
              >Uke {{ w.week }}</div>
            </div>
            <!-- Header: days -->
            <div class="cp-row cp-head cp-head-days">
              <div class="cp-name cp-sticky small text-muted" :style="{ width: nameWidth + 'px' }">Fartøy</div>
              <div
                v-for="d in days"
                :key="d.date"
                class="cp-day"
                :class="{ 'cp-weekend': d.weekend, 'cp-today': d.date === plan.today }"
                :style="{ width: dayWidth + 'px' }"
                :title="d.title"
              >
                <div class="cp-day-letter">{{ d.letter }}</div>
                <div>{{ d.num }}</div>
              </div>
            </div>

            <!-- One row per vessel -->
            <div v-for="v in visibleVessels" :key="v.id" class="cp-row cp-vessel">
              <div class="cp-name cp-sticky" :style="{ width: nameWidth + 'px' }">
                <div class="d-flex align-items-start">
                  <div class="flex-grow-1 overflow-hidden">
                    <div class="font-weight-bold text-truncate">{{ v.name }}</div>
                    <div class="small text-muted text-truncate">
                      <template v-if="v.rule">
                        {{ modeLabel(v.rule.mode) }}
                        <span v-if="v.rule.mode === 'periode'">· {{ v.rule.period_days }} dager</span>
                      </template>
                      <template v-else>Ingen rotasjonsregel</template>
                    </div>
                  </div>
                  <b-button
                    size="sm"
                    variant="link"
                    class="p-0 ml-1"
                    title="Rotasjonsregel"
                    @click="openRotation(v)"
                  ><i class="ion ion-ios-settings"></i></b-button>
                </div>
              </div>

              <div
                class="cp-lane"
                :style="laneStyle(v)"
              >
                <!-- On-duty band -->
                <div
                  v-for="(s, i) in v.segments"
                  :key="'s' + i"
                  class="cp-band"
                  :style="barPos(s.from, s.to, { background: tint(skiftById[s.skift]), top: '4px', height: bandHeight + 'px' })"
                  :title="skiftName(s.skift) + ': ' + fmt(s.period_from) + ' – ' + fmt(s.period_to)"
                >
                  <span class="cp-band-label" :style="{ color: colorOf(s.skift) }">{{ skiftName(s.skift) }}</span>
                </div>

                <!-- Orders -->
                <div
                  v-for="item in layout(v)"
                  :key="item.order.id"
                  class="cp-order"
                  :class="{ 'cp-order-unmanaged': !item.order.managed, 'cp-order-selected': selected && selected.order.id === item.order.id }"
                  :style="barPos(item.order.startdate, item.order.enddate, orderStyle(item))"
                  :title="orderTooltip(item.order)"
                  @click="openOrder(v, item.order)"
                >
                  <span class="cp-order-text">
                    <i v-if="item.order.crew_locked" class="ion ion-ios-lock mr-1"></i>
                    <b>{{ item.order.ordernumber }}</b>
                    <span v-if="item.order.skift_name"> · {{ item.order.skift_name }}</span>
                    <span class="cp-order-crew"> · {{ item.order.crew.length }} pers.</span>
                  </span>
                  <i v-if="item.order.changes.length" class="ion ion-ios-swap cp-order-icon" title="Har mannskapsendringer"></i>
                  <i v-if="item.order.warnings.length" class="ion ion-ios-warning cp-order-icon cp-order-warn" title="Har varsler"></i>
                </div>

                <!-- Today marker -->
                <div v-if="todayOffset !== null" class="cp-today-line" :style="{ left: todayOffset + 'px' }"></div>

                <div v-if="!v.orders.length" class="cp-empty small text-muted" :style="{ top: bandHeight + 12 + 'px' }">
                  Ingen ordrer i perioden
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-tab>

      <!-- Absences and changes -->
      <b-tab :title="'Fravær og endringer' + (plan ? ' (' + plan.changes.length + ')' : '')">
        <crew-changes
          v-if="plan"
          :plan="plan"
          @edit="(c) => openChange(null, null, c)"
          @changed="load()"
        />
      </b-tab>
    </b-tabs>

    <order-crew-panel
      :selected="selected"
      :plan="plan"
      @close="selected = null"
      @changed="load()"
      @change-crew="(crewOut) => openChange(selected.vessel, selected.order, null, crewOut)"
    />

    <crew-change-modal
      ref="changeModal"
      :plan="plan"
      @saved="load()"
    />

    <vessel-rotation-modal
      ref="rotationModal"
      :plan="plan"
      @saved="load()"
    />
  </div>
</template>

<script>
import dayjs from "dayjs";
import api, { modeLabel, skiftColor, errorText } from "./api";
import OrderCrewPanel from "./OrderCrewPanel.vue";
import CrewChangeModal from "./CrewChangeModal.vue";
import VesselRotationModal from "./VesselRotationModal.vue";
import CrewChanges from "./CrewChanges.vue";

const STORAGE_KEY = "mannskapsplan-view";

export default {
  name: "Mannskapsplanlegger",
  components: { OrderCrewPanel, CrewChangeModal, VesselRotationModal, CrewChanges },
  metaInfo: { title: "Mannskapsplanlegger" },
  data() {
    const saved = this.readView();
    return {
      plan: null,
      isLoading: false,
      tab: 0,
      start: dayjs().startOf("isoWeek").format("YYYY-MM-DD"),
      weeks: saved.weeks || 8,
      vesselFilter: saved.vesselFilter || null,
      weekOptions: [
        { value: 4, text: "4 uker" },
        { value: 8, text: "8 uker" },
        { value: 12, text: "12 uker" },
        { value: 26, text: "26 uker" },
      ],
      selected: null, // { vessel, order }
      narrow: window.innerWidth < 576,
      bandHeight: 20,
      rowHeight: 28,
    };
  },
  computed: {
    nameWidth() {
      return this.narrow ? 120 : 190;
    },
    end() {
      return dayjs(this.start).add(this.weeks * 7 - 1, "day").format("YYYY-MM-DD");
    },
    dayWidth() {
      return this.weeks > 12 ? 14 : this.weeks > 8 ? 24 : 32;
    },
    days() {
      const out = [];
      const letters = ["S", "M", "T", "O", "T", "F", "L"];
      for (let i = 0; i < this.weeks * 7; i++) {
        const d = dayjs(this.start).add(i, "day");
        out.push({
          date: d.format("YYYY-MM-DD"),
          num: d.date(),
          letter: this.dayWidth >= 24 ? letters[d.day()] : "",
          weekend: d.day() === 0 || d.day() === 6,
          title: d.format("dddd D. MMMM YYYY"),
        });
      }
      return out;
    },
    weekHeaders() {
      const out = [];
      for (let i = 0; i < this.weeks; i++) {
        const d = dayjs(this.start).add(i * 7, "day");
        out.push({ key: d.format("YYYY-MM-DD"), week: d.isoWeek(), days: 7 });
      }
      return out;
    },
    rangeLabel() {
      return dayjs(this.start).format("D. MMM") + " – " + dayjs(this.end).format("D. MMM YYYY");
    },
    todayOffset() {
      if (!this.plan) return null;
      const i = dayjs(this.plan.today).diff(dayjs(this.start), "day");
      return i >= 0 && i < this.days.length ? i * this.dayWidth + this.dayWidth / 2 : null;
    },
    skiftById() {
      const map = {};
      ((this.plan && this.plan.skifts) || []).forEach((s, i) => {
        map[s.id] = { ...s, color: skiftColor(s, i) };
      });
      return map;
    },
    legendSkifts() {
      const used = new Set();
      this.visibleVessels.forEach((v) => {
        (v.rule ? v.rule.skifts : []).forEach((id) => used.add(id));
        v.orders.forEach((o) => o.skift && used.add(o.skift));
      });
      return [...used].map((id) => this.skiftById[id]).filter(Boolean);
    },
    vesselOptions() {
      const vessels = (this.plan && this.plan.vessels) || [];
      return [{ value: null, text: "Alle fartøy" }].concat(vessels.map((v) => ({ value: v.id, text: v.name })));
    },
    visibleVessels() {
      const vessels = (this.plan && this.plan.vessels) || [];
      return this.vesselFilter ? vessels.filter((v) => v.id === this.vesselFilter) : vessels;
    },
  },
  watch: {
    vesselFilter() {
      this.saveView();
    },
    weeks() {
      this.saveView();
    },
  },
  methods: {
    modeLabel,
    async load() {
      this.isLoading = true;
      try {
        this.plan = await api.getPlan({ from: this.start, to: this.end });
        // Keep the side panel in sync with fresh data.
        if (this.selected) {
          const v = this.plan.vessels.find((x) => x.id === this.selected.vessel.id);
          const o = v && v.orders.find((x) => x.id === this.selected.order.id);
          this.selected = v && o ? { vessel: v, order: o } : null;
        }
      } catch (error) {
        console.log(error);
        this.$bvToast.toast("Kunne ikke laste mannskapsplanen. " + errorText(error), {
          title: "Feil",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.isLoading = false;
      }
    },
    shift(weeks) {
      this.start = dayjs(this.start).add(weeks * 7, "day").format("YYYY-MM-DD");
      this.load();
    },
    goToday() {
      this.start = dayjs().startOf("isoWeek").format("YYYY-MM-DD");
      this.load();
    },
    readView() {
      try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
      } catch (e) {
        return {};
      }
    },
    onResize() {
      this.narrow = window.innerWidth < 576;
    },
    saveView() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ weeks: this.weeks, vesselFilter: this.vesselFilter }));
      } catch (e) {
        // ignore: view settings are a convenience only
      }
    },

    // ---- layout helpers
    dayIndex(date) {
      return dayjs(date).diff(dayjs(this.start), "day");
    },
    barPos(from, to, extra = {}) {
      const first = Math.max(0, this.dayIndex(from));
      const last = Math.min(this.days.length - 1, this.dayIndex(to || from));
      return {
        ...extra,
        left: first * this.dayWidth + 1 + "px",
        width: Math.max(1, last - first + 1) * this.dayWidth - 2 + "px",
      };
    },
    // Stack overlapping orders in rows.
    layout(vessel) {
      const rows = [];
      return vessel.orders
        .slice()
        .sort((a, b) => (a.startdate < b.startdate ? -1 : a.startdate > b.startdate ? 1 : 0))
        .map((order) => {
          let row = rows.findIndex((endDate) => endDate < order.startdate);
          if (row === -1) {
            row = rows.length;
            rows.push(order.enddate);
          } else {
            rows[row] = order.enddate;
          }
          return { order, row };
        });
    },
    laneStyle(vessel) {
      const rows = Math.max(1, ...this.layout(vessel).map((i) => i.row + 1));
      const height = this.bandHeight + 12 + rows * this.rowHeight + 6;
      const w = this.dayWidth;
      return {
        width: this.days.length * w + "px",
        height: height + "px",
        // Day grid lines and weekend shading (weeks always start on Monday).
        backgroundImage:
          `linear-gradient(to right, rgba(0,0,0,.05) 1px, transparent 1px),` +
          `repeating-linear-gradient(to right, transparent 0, transparent ${5 * w}px, rgba(0,0,0,.035) ${5 * w}px, rgba(0,0,0,.035) ${7 * w}px)`,
        backgroundSize: `${w}px 100%, 100% 100%`,
      };
    },
    orderStyle(item) {
      const color = item.order.managed ? this.colorOf(item.order.skift) : "#adb5bd";
      return {
        top: this.bandHeight + 12 + item.row * this.rowHeight + "px",
        height: this.rowHeight - 4 + "px",
        background: color,
      };
    },
    colorOf(skiftId) {
      return (this.skiftById[skiftId] && this.skiftById[skiftId].color) || "#6c757d";
    },
    tint(skift) {
      // 18% opacity version of the skift colour for the on-duty band.
      const hex = (skift && skift.color) || "#6c757d";
      return hex.length === 7 ? hex + "2e" : hex;
    },
    skiftName(id) {
      return (this.skiftById[id] && this.skiftById[id].name) || "Ukjent skift";
    },
    fmt(date) {
      return date ? dayjs(date).format("DD.MM.YYYY") : "";
    },
    orderTooltip(o) {
      const lines = [
        o.title,
        this.fmt(o.startdate) + " – " + this.fmt(o.enddate),
        o.status,
        o.skift_name ? "Skift: " + o.skift_name : "Uten skift",
        "Mannskap: " + (o.crew.map((c) => c.name).join(", ") || "ingen"),
      ];
      o.warnings.forEach((w) => lines.push("⚠ " + w.text));
      return lines.join("\n");
    },

    // ---- actions
    openOrder(vessel, order) {
      this.selected = { vessel, order };
    },
    openRotation(vessel) {
      this.$refs.rotationModal.open(vessel);
    },
    openChange(vessel, order, change = null, crewOut = null) {
      this.$refs.changeModal.open({ vessel, order, change, crewOut });
    },
  },
  created() {
    this.load();
    window.addEventListener("resize", this.onResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
  },
};
</script>

<style lang="css">
.cp-scroll {
  overflow-x: auto;
  max-height: calc(100vh - 280px);
  overflow-y: auto;
}
.cp-loading {
  opacity: 0.6;
  pointer-events: none;
}
.cp-grid {
  position: relative;
}
.cp-row {
  display: flex;
}
.cp-sticky {
  position: sticky;
  left: 0;
  z-index: 3;
  background: #fff;
}
.cp-name {
  flex: 0 0 auto;
  padding: 6px 10px;
  border-right: 1px solid #e3e6ea;
}
.cp-head {
  position: sticky;
  top: 0;
  z-index: 4;
  background: #fff;
}
.cp-head-days {
  top: 26px;
  border-bottom: 1px solid #e3e6ea;
}
.cp-head .cp-name {
  z-index: 5;
}
.cp-week {
  flex: 0 0 auto;
  height: 26px;
  line-height: 26px;
  font-size: 12px;
  font-weight: 600;
  padding-left: 6px;
  border-left: 1px solid #e3e6ea;
  background: #f8f9fa;
}
.cp-day {
  flex: 0 0 auto;
  text-align: center;
  font-size: 11px;
  line-height: 1.2;
  padding: 3px 0;
  color: #495057;
}
.cp-day-letter {
  color: #adb5bd;
  font-size: 10px;
}
.cp-weekend {
  background: #f8f9fa;
}
.cp-today {
  background: #fff3cd;
  font-weight: 700;
}
.cp-vessel {
  border-bottom: 1px solid #e3e6ea;
}
.cp-lane {
  position: relative;
  flex: 0 0 auto;
}
.cp-band {
  position: absolute;
  border-radius: 4px;
  overflow: hidden;
  white-space: nowrap;
}
.cp-band-label {
  font-size: 11px;
  font-weight: 700;
  padding-left: 6px;
  line-height: 20px;
}
.cp-order {
  position: absolute;
  border-radius: 4px;
  color: #fff;
  font-size: 12px;
  padding: 0 6px;
  display: flex;
  align-items: center;
  cursor: pointer;
  overflow: hidden;
  white-space: nowrap;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
  transition: box-shadow 0.15s, transform 0.15s;
}
.cp-order:hover {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
}
.cp-order-selected {
  outline: 2px solid #212529;
  outline-offset: 1px;
}
.cp-order-unmanaged {
  background-image: repeating-linear-gradient(45deg, transparent 0 6px, rgba(255, 255, 255, 0.25) 6px 12px);
}
.cp-order-text {
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cp-order-icon {
  margin-left: 4px;
  font-size: 14px;
}
.cp-order-warn {
  color: #ffe08a;
}
.cp-today-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  margin-left: -1px;
  background: #e74a3b;
  opacity: 0.6;
  z-index: 2;
  pointer-events: none;
}
.cp-empty {
  position: absolute;
  left: 8px;
}
.cp-legend-swatch {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 3px;
}
.cp-legend-unmanaged {
  background: #adb5bd repeating-linear-gradient(45deg, transparent 0 3px, rgba(255, 255, 255, 0.4) 3px 6px);
}
</style>
